import { useTranslation } from 'next-i18next';
import { useFormContext } from 'react-hook-form';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { ICompanySettings } from '@/modules/settings/company/types';
import { InputField, NextImage, SelectField } from '@/shared/components/atoms';
import GeoLocationPicker from '@/shared/components/molecules/GeoLocationPicker';
import { Countries, getCountryIcon } from '@/shared/lib/country-flags';

const AddPropertyBasicInfo = () => {
  const { t } = useTranslation();

  const {
    register,
    formState: { isSubmitting, errors },
    watch,
    setValue,
  } = useFormContext<ICompanySettings.IAddPropertyFormValues>();
  const type = watch('type');

  return (
    <div className='grid grid-cols-2 gap-24'>
      <InputField
        borderColor='border-outline-variant'
        width='w-full'
        py='!py-16'
        label={t('Property Name')}
        placeholder={t('Property Name')}
        error={!!errors.name}
        errorMessage={errors.name?.message}
        className='!rounded-xl'
        disabled={isSubmitting}
        {...register('name')}
      />
      <SelectField
        id='type'
        getLabel={(obj: { label: string; value: string }) => obj.label}
        getValue={(obj: { label: string; value: string }) => obj.value}
        className='!rounded-xl bg-background-transparent'
        borderColor={themeColors().outline.variant}
        label={t('Property Type')}
        placeholder={t('Property Type')}
        isMulti={false}
        list={[
          { label: t('farm'), value: 'Farm' },
          { label: t('packhouse'), value: 'Packhouse' },
        ]}
        py='10px'
        px='0.5rem'
        borderRadius='1rem'
        width='w-full'
      />
      {type === 'Farm' && (
        <InputField
          borderColor='border-outline-variant'
          width='w-full'
          py='!py-16'
          label={t('area')}
          placeholder={t('area')}
          error={!!errors.totalArea}
          errorMessage={errors.totalArea?.message}
          className='!rounded-xl bg-background-transparent'
          disabled={isSubmitting}
          {...register('totalArea')}
          type='number'
          step='0.01'
        />
      )}
      <GeoLocationPicker id='location' label={t('location')} />
      <SelectField<ICompanySettings.IPropertyCountry, false>
        id='country'
        getLabel={(obj: ICompanySettings.IPropertyCountry) => obj.label}
        getValue={(obj: ICompanySettings.IPropertyCountry) => {
          setValue('timeZone', obj.timeZone[0] ?? '');
          setValue('currency', obj.currency ?? '');
          return obj.value;
        }}
        className='!rounded-xl bg-background-transparent'
        borderColor={themeColors().outline.variant}
        label={t('country')}
        isMulti={false}
        list={Countries.countries}
        placeholder={t('Country')}
        py='10px'
        px='0.5rem'
        borderRadius='1rem'
        width='w-full'
        formatOptionLabel={(option: ICompanySettings.IPropertyCountry) => (
          <div className='flex items-center gap-12'>
            <NextImage
              src={getCountryIcon(option.value)}
              width={24}
              height={16}
              alt='country flag'
            />
            {option.label}
          </div>
        )}
      />
    </div>
  );
};

export { AddPropertyBasicInfo };
